import React from 'react';
import { useTranslation } from 'react-i18next';

import PageLayout from '@/components/layout/PageLayout';
import Table from '@/components/ui/tables/Table';
import { DateRangePicker, FilterDropdown } from '@/components/ui/filters';
import { Selector } from '@/components/ui/inputs/selector';
import { PrimaryButton } from '@/components/ui/buttons';
import { IconScout } from '@/components/ui/images/IconScout';
import { useAsyncJobList } from '@/hooks/asyncJob/useAsyncJobList';


export const AsyncJobListPage: React.FC = () => {
  const { t: translate } = useTranslation();

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    isInitializing,
    columns,
    expandable,
    onSearch,
    onSortChange,
    filters,
    handleDateRangeChange,
    handleProcessChange,
    handleJobTypeChange,
    processOptions,
    jobTypeOptions,
    refetch,
  } = useAsyncJobList();

  return (
    <PageLayout isLoading={isInitializing || (loading && rows.length === 0)}>
      <div className="flex flex-col gap-4" data-testid="async-job-list-page">

        <div className="flex flex-wrap items-end gap-4">
          <DateRangePicker
            label={`${translate('asyncJob:filters.dateFrom')} - ${translate('asyncJob:filters.dateTo')}`}
            value={{ from: filters.dateFrom ?? undefined, to: filters.dateTo ?? undefined }}
            onChange={handleDateRangeChange}
            maxRangeDays={30}
          />

          <FilterDropdown
            renderExtraFilters={() => (
              <div className="flex flex-col gap-3">
                <Selector
                  label={translate('asyncJob:filters.process')}
                  value={filters.process ?? ''}
                  options={processOptions}
                  onChange={handleProcessChange}
                />
                <Selector
                  label={translate('asyncJob:filters.type')}
                  value={filters.jobType ?? ''}
                  options={jobTypeOptions}
                  onChange={handleJobTypeChange}
                />
              </div>
            )}
          />

          <PrimaryButton onClick={refetch} isLoading={loading} className="ml-auto">
            <span className="flex items-center gap-2">
              <IconScout name="sync" size={18} color="white" />
              {translate('asyncJob:actions.reload')}
            </span>
          </PrimaryButton>
        </div>


        <Table
          columns={columns}
          data={rows}
          totalRows={total}
          isLoading={loading}
          searchable
          onSearch={onSearch}
          serverSideSort
          initialSort={{ key: 'createdAt', direction: 'desc' }}
          onSortChange={onSortChange}
          pagination={{
            page,
            pageSize,
            total,
            onChange: setPage,
            onPageSizeChange: setPageSize,
          }}
          expandable={expandable}
          data-testid="async-job-list-table"
          emptyMessage={translate('asyncJob:messages.noData')}
        />

      </div>
    </PageLayout>
  );
};

export default AsyncJobListPage;
